// Pure conversions from the api's answers (src/api/schema.d.ts) to the types
// the components take (src/data/mock.ts). No fetching, no React: every
// function here is tested on its own (tests/adapt.test.ts).
import { isApiError } from "../../api/client";
import type { components } from "../../api/schema";
import type { BulkMode } from "../../components/sidebar/panels";
import type { Selection } from "../../components/sidebar/SourceList";
import { logoFor } from "../../data/logos";
import type { IterRow, NewsItem, ReaderChunk, ReaderFile, SavedGroup, Source, SubSource } from "../../data/mock";
import { formatDateTime, formatLongDate, formatTime, monthName, romeYear, weekLabel } from "../../lib/dates";
import { initialsOf } from "../../lib/initials";
import { fromOf } from "../../lib/navigation";
import { withNewsParams, type SavedFilter } from "./params";

export type Catalogue = components["schemas"]["Catalogue"];
export type CatalogueOrganisation = components["schemas"]["CatalogueOrganisation"];
export type CatalogueSection = components["schemas"]["CatalogueSection"];
export type FollowChange = components["schemas"]["FollowChange"];
export type FeedCard = components["schemas"]["FeedCard"];
export type SavedCard = components["schemas"]["SavedCard"];
export type ItemDetail = components["schemas"]["ItemDetail"];
export type FeedPage = components["schemas"]["FeedPage"];
/** One row of a feed list: a card, or a sitting of the Parliament (`kind` tells them apart). */
export type FeedItem = FeedPage["items"][number];
export type FeedSitting = components["schemas"]["FeedSitting"];
export type SavedPage = components["schemas"]["SavedPage"];
export type Visit = components["schemas"]["Visit"];

/** `origin:source_key`, the key the feed filters and the favourites use. */
export const sourceKey = (card: Pick<FeedCard, "origin" | "source_key">) => `${card.origin}:${card.source_key}`;

/** The id a card has in the lists: origin and id together, as the item endpoints take them. */
export const itemId = (card: Pick<FeedCard, "origin" | "id">) => `${card.origin}/${card.id}`;

/** The path of a card's own page: /news/<origin>/<id>, the id percent-encoded (Coro's carry colons). */
export const itemPath = (card: Pick<FeedCard, "origin" | "id">) =>
  `/news/${card.origin}/${encodeURIComponent(card.id)}`;

/**
 * Where «Torna alle news» leads: the location the page was opened from
 * (`state.from` of the link, pathname and query string) when it is a path of
 * the News area, of the Parliament chapter (a sitting's page) or the search
 * (/ricerca, with the same parameters), the plain list otherwise (a URL
 * typed or shared). The opened cards and the scroll are not restored
 * (accepted).
 */
export function backPath(state: unknown): string {
  const from = fromOf(state);
  return from !== null && /^\/(news|parlamento|ricerca)(?![\w-])/.test(from) ? from : "/news";
}

/**
 * The label of the way back: «Torna alla ricerca» from the search, «Torna
 * alla seduta» from a sitting's page, «Torna alle news» otherwise.
 */
export function backLabel(path: string): string {
  if (/^\/ricerca(?![\w-])/.test(path)) return "Torna alla ricerca";
  return /^\/parlamento\/[^/]+\/[^/]+\/\d+/.test(path) ? "Torna alla seduta" : "Torna alle news";
}

export type Origin = FeedCard["origin"];
/** Every origin of the contract: a new one fails the typecheck here until it is added. */
const ORIGINS = { orchestra: true, coro: true } satisfies Record<Origin, true>;
const isOrigin = (value: string): value is Origin => Object.hasOwn(ORIGINS, value);

/**
 * The inverse of itemId: the origin is everything before the first slash, the
 * id the rest (Coro's ids carry colons, never a leading slash). Null when the
 * string is not an id of the lists (no slash, an unknown origin, no id).
 */
export function parseItemId(value: string): { origin: Origin; id: string } | null {
  const slash = value.indexOf("/");
  if (slash < 0) return null;
  const origin = value.slice(0, slash);
  const id = value.slice(slash + 1);
  return isOrigin(origin) && id !== "" ? { origin, id } : null;
}

/** "12 ottobre 2026 (scaricato alle 09:32)"; without a published date, only the download time. */
export function publishedLabel(card: Pick<FeedCard, "published_at" | "first_seen_at">): string {
  const downloaded = `scaricato alle ${formatTime(card.first_seen_at)}`;
  return card.published_at ? `${formatLongDate(card.published_at)} (${downloaded})` : downloaded;
}

/** The two chips of a card as in-app paths: the organisation's view and the section's view. */
export interface CardLinks {
  organisation: string;
  section: string;
}

/**
 * The paths of a card's chips. The section's view is the card's own key; the
 * organisation's view is every active section of the organisation the key
 * belongs to in the tree, or the card's key alone until the catalogue has
 * arrived (or when the key is in no organisation, e.g. a removed section).
 * `search` is the current query string: the view and the tab carry over.
 */
export function cardLinks(
  card: Pick<FeedCard, "origin" | "source_key">,
  tree: Pick<SourceTree, "active"> | null,
  search: URLSearchParams = new URLSearchParams(),
): CardLinks {
  const key = sourceKey(card);
  const organisation = tree?.active.find((org) => subtreeKeys(org).includes(key));
  const path = (keys: string[]) => `/news?${withNewsParams(search, { tab: "latest", sources: keys })}`;
  return { organisation: path(organisation ? subtreeKeys(organisation) : [key]), section: path([key]) };
}

/**
 * What the avatar of a card shows for its source: the organisation's initials,
 * and its logo when Platea has one for the source's domain (src/data/logos.ts).
 * Without a logo the key is left out, so the objects compare as before.
 */
export function sourceBadge(name: string, domain: string | null | undefined): { initials: string; logo?: string } {
  const logo = logoFor(domain);
  return { initials: initialsOf(name), ...(logo ? { logo } : {}) };
}

/**
 * A card of the feed or of News salvate as the NewsCard component takes it:
 * neutral text, no `strong`; the chips lead to the views of the card's
 * organisation and section (cardLinks). The body is empty: the card opened
 * in place shows the text of the detail (toItemBody), not the excerpt.
 */
export function toNewsItem(
  card: FeedCard | SavedCard,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
): NewsItem {
  const excerpt = card.excerpt ?? card.preview ?? "";
  const landing = card.source?.landing_url ?? "";
  const links = cardLinks(card, tree, search);
  const name = card.source?.organisation ?? card.outlet ?? "";
  return {
    id: itemId(card),
    source: { name, url: links.organisation, ...sourceBadge(name, card.source?.domain) },
    channel: { name: card.source?.section ?? "", url: links.section },
    hasCode: card.has_text,
    publishedLabel: publishedLabel(card),
    title: card.title ?? "",
    excerpt: excerpt ? [{ text: excerpt }] : [],
    body: [],
    url: card.url ?? landing,
    pageUrl: itemPath(card),
    saved: card.saved,
    // always in place, as a preview: whole when short, with «Leggi altro» otherwise (toItemBody)
    chevron: "down",
  };
}

/** The paragraphs of an item's text, split on blank lines (then single line breaks); neutral runs. */
export function toBody(text: string | null | undefined): NewsItem["body"] {
  if (!text) return [];
  const blocks = text.includes("\n\n") ? text.split(/\n{2,}/) : text.split(/\n/);
  return blocks
    .map((b) => b.trim())
    .filter(Boolean)
    .map((b) => [{ text: b }]);
}

/**
 * The longest text a card shows in place, in characters (code points, so an
 * accented letter or an emoji counts once): from this length on the text
 * belongs to the card's own page (brief F2, «Chevron»).
 */
export const IN_PLACE_MAX_CHARS = 2400;

/**
 * Whether the detail's text fits the card opened in place: a text, no files,
 * and shorter than IN_PLACE_MAX_CHARS. Otherwise the text belongs to the
 * card's page, which does not exist yet: the card says so instead.
 */
export function opensInPlace(detail: Pick<ItemDetail, "has_text" | "files" | "text">): boolean {
  return detail.has_text && detail.files.length === 0 && detail.text !== null && isShortText(detail.text);
}

/**
 * Whether a text is under IN_PLACE_MAX_CHARS code points. A string has at
 * least as many UTF-16 units as code points and at most twice as many, so
 * the code points are counted only between the two bounds (a text may weigh
 * megabytes).
 */
function isShortText(text: string): boolean {
  if (text.length < IN_PLACE_MAX_CHARS) return true;
  if (text.length >= 2 * IN_PLACE_MAX_CHARS) return false;
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i);
    // a high surrogate followed by a low one is one code point
    if (unit >= 0xd800 && unit <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) i++;
    }
    if (++count >= IN_PLACE_MAX_CHARS) return false;
  }
  return true;
}

/**
 * Whether the text is not the whole item: missing (`has_text` false, `text`
 * null or blank) or kept short on purpose by the source. `text_policy` is
 * null for a source whose text is the item's own text; a value names why the
 * text is not there or is only a preview. Loggione writes `metadata_only`
 * (archetype 10: title and preview only) and `pdf_only` (a bulletin whose text
 * is in the PDF); there is no value for «full text». Any value counts, so a
 * policy added later reads as incomplete rather than as complete.
 */
export function isTextIncomplete(detail: Pick<ItemDetail, "has_text" | "text" | "text_policy">): boolean {
  return !detail.has_text || !detail.text?.trim() || detail.text_policy !== null;
}

/** What stands in the card opened in place above (or instead of) the text. */
export type ItemNotice = "incomplete";

/**
 * How much of a long text the preview of a card shows, at most (about 300
 * words); a text a little longer is there whole (IN_PLACE_MAX_CHARS), so a
 * preview never stops a few lines from the end.
 */
export const PREVIEW_CHARS = 1800;

/** The shortest a preview cut at a paragraph or a sentence may be, as a share of PREVIEW_CHARS. */
const PREVIEW_MIN_SHARE = 0.6;

/**
 * The beginning of a text, for the preview, at most PREVIEW_CHARS: cut at the
 * end of the last paragraph, or else of the last sentence, that ends past
 * PREVIEW_MIN_SHARE of them; otherwise at the last space, with «…» (or at
 * PREVIEW_CHARS when there is none).
 */
export function previewText(text: string): string {
  const head = text.slice(0, PREVIEW_CHARS);
  const min = PREVIEW_CHARS * PREVIEW_MIN_SHARE;
  const paragraph = head.lastIndexOf("\n");
  if (paragraph >= min) return head.slice(0, paragraph).trimEnd();
  let sentence = -1;
  for (const match of head.matchAll(/[.!?;:»”"')\]](?=\s)/g)) sentence = match.index + 1;
  if (sentence >= min) return head.slice(0, sentence);
  const space = head.search(/\s\S*$/);
  return `${(space > 0 ? head.slice(0, space) : head).trimEnd()}…`;
}

/**
 * The opened card's content from the detail. A text that fits in place
 * (opensInPlace) is there whole; otherwise the card is a preview, the
 * beginning of the text (previewText, nothing when there is no text), and
 * `more` asks for «Leggi altro» to the card's page, where the rest and the
 * files are. «incomplete» whenever the text is missing or partial.
 */
export function toItemBody(detail: Pick<ItemDetail, "has_text" | "files" | "text" | "text_policy">): {
  body: NewsItem["body"];
  notices: ItemNotice[];
  more: boolean;
} {
  const notices: ItemNotice[] = isTextIncomplete(detail) ? ["incomplete"] : [];
  if (opensInPlace(detail)) return { body: toBody(detail.text), notices, more: false };
  const text = detail.has_text && detail.text?.trim() ? detail.text : null;
  const body = text === null ? [] : toBody(isShortText(text) ? text : previewText(text));
  return { body, notices, more: true };
}

/** The label of «News dall'ultimo accesso»: the visit's `since` as dd/MM/yyyy - HH:mm in Europe/Rome. */
export function lastAccessLabel(visit: Pick<Visit, "since">): string {
  return formatDateTime(visit.since);
}

// ---- The page of a news -----------------------------------------------------

export type ItemFile = components["schemas"]["ItemFile"];

/** The longest block of the reader, in UTF-16 units: a paragraph past it is split at its sentences. */
export const READER_BLOCK_MAX_CHARS = 3000;
/** How much text a chunk of the reader holds, about: blocks are never split between chunks. */
export const READER_CHUNK_CHARS = 40_000;

/**
 * Where a sentence ends and the next begins: a full stop, !, ?, ; or :
 * followed by spaces and a capital, an accented capital, a quote, a bracket
 * or a digit. Global, so one pass over a paragraph finds every boundary.
 */
const SENTENCE_BOUNDARY = /(?<=[.!?;:])\s+(?=[A-ZÀ-ÝÈÉÌÒÙ«"(\d])/g;

/** The characters (code points) of a string: an accented letter or an emoji counts once. */
export function charCount(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) i++;
    }
    count++;
  }
  return count;
}

/**
 * One paragraph as pieces of at most READER_BLOCK_MAX_CHARS: cut at the last
 * sentence boundary before the limit, at the nearest space before it when a
 * sentence is longer than that, and hard at the limit as the last resort (a
 * run without spaces). A text from a PDF is often one line of a million
 * characters: this is what makes it paragraphs.
 */
function splitParagraph(paragraph: string): string[] {
  if (paragraph.length <= READER_BLOCK_MAX_CHARS) return [paragraph];
  // every boundary once: [end of the sentence, start of the next]
  const boundaries: [number, number][] = [];
  for (const match of paragraph.matchAll(SENTENCE_BOUNDARY)) {
    boundaries.push([match.index, match.index + match[0].length]);
  }
  const pieces: string[] = [];
  let start = 0;
  let next = 0; // the first boundary at or past `start`
  while (paragraph.length - start > READER_BLOCK_MAX_CHARS) {
    const limit = start + READER_BLOCK_MAX_CHARS;
    let cut: [number, number] | null = null;
    let i = next;
    while (i < boundaries.length && boundaries[i]![0] <= limit) {
      if (boundaries[i]![0] > start) cut = boundaries[i]!;
      i++;
    }
    if (cut === null) {
      const space = paragraph.lastIndexOf(" ", limit);
      cut = space > start ? [space, space + 1] : [limit, limit];
    }
    pieces.push(paragraph.slice(start, cut[0]).trim());
    start = cut[1];
    while (next < boundaries.length && boundaries[next]![0] < start) next++;
  }
  pieces.push(paragraph.slice(start).trim());
  return pieces.filter(Boolean);
}

/** The blocks of the reader: the paragraphs of the text (toBody), the long ones split at their sentences. */
export function toReaderBlocks(text: string | null | undefined): string[] {
  return toBody(text).flatMap((paragraph) => splitParagraph(paragraph.map((run) => run.text).join("")));
}

/**
 * The blocks in chunks of about READER_CHUNK_CHARS characters: a block never
 * spans two chunks, so a chunk may run past the limit by one block. The
 * reader shows the first chunk and adds the next on demand.
 */
export function toReaderChunks(blocks: string[]): ReaderChunk[] {
  const chunks: ReaderChunk[] = [];
  let current: ReaderChunk = { blocks: [], chars: 0 };
  for (const block of blocks) {
    const chars = charCount(block);
    if (current.blocks.length > 0 && current.chars + chars > READER_CHUNK_CHARS) {
      chunks.push(current);
      current = { blocks: [], chars: 0 };
    }
    current.blocks.push(block);
    current.chars += chars;
  }
  if (current.blocks.length > 0) chunks.push(current);
  return chunks;
}

/** What the page says above the text: the text is partial, missing, or a file needs to be read by hand. */
export type ReaderNotice = "incomplete" | "missing" | "manual-read";

/** The page of a news as NewsReader takes it (the types of the rows are in src/data/mock.ts). */
export interface ItemPage {
  news: NewsItem;
  /** item_type, doc_number, legislature, chamber, the versions and the last change: only the ones that are there. */
  metadata: string[];
  notices: ReaderNotice[];
  files: ReaderFile[];
  iter: IterRow[];
  chunks: ReaderChunk[];
  /** The characters of every chunk together: «Mostrati N di <totalChars> caratteri». */
  totalChars: number;
}

const FILE_KINDS: [RegExp, string][] = [
  [/pdf/, "PDF"],
  [/msword|wordprocessingml|\/rtf/, "Word"],
  [/ms-excel|spreadsheetml/, "Excel"],
  [/zip/, "ZIP"],
  [/html/, "HTML"],
];

/** PDF, Word, Excel, ZIP, HTML from the content type, else «file». */
export function fileKind(contentType: string | null): string {
  const type = (contentType ?? "").toLowerCase();
  return FILE_KINDS.find(([pattern]) => pattern.test(type))?.[1] ?? "file";
}

const kilobytes = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const megabytes = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** «953 kB» under a megabyte, «1,6 MB» from there; empty when unknown. */
export function fileSize(bytes: number | null): string {
  if (bytes === null || bytes < 0) return "";
  if (bytes < 1_000_000) return `${kilobytes.format(bytes / 1000)} kB`;
  return `${megabytes.format(bytes / 1_000_000)} MB`;
}

const CHAMBER_LABELS: Record<string, string> = { camera: "Camera", senato: "Senato" };
/** «Camera» / «Senato» from the api's chamber; another value as it is. */
const chamberLabel = (chamber: string | null | undefined) =>
  chamber ? (CHAMBER_LABELS[chamber.toLowerCase()] ?? chamber) : null;

/** The download route of a file, on the api's origin: a link to it gets a 302 to the file. */
export function fileDownloadUrl(
  apiBase: string,
  item: Pick<ItemDetail, "origin" | "id">,
  file: Pick<ItemFile, "file_id">,
): string {
  const id = encodeURIComponent(item.id);
  return `${apiBase}/api/v1/items/${item.origin}/${id}/files/${encodeURIComponent(file.file_id)}/download`;
}

/** What an origin's chapter decides about the page's metadata line. */
export interface ItemPageOptions {
  /** The label of `item_type`: the type itself by default (the Parliament chapter hands its Italian labels). */
  typeLabel?: (itemType: string) => string;
  /**
   * Whether the versions and the last change stand in the metadata line
   * (default). The Parliament chapter says them in its own section, in its
   * own words (a record that grew during the sitting is not «57 versioni»).
   */
  revisions?: boolean;
}

/**
 * The page of a news from its detail: the card's head (toNewsItem), the
 * generic metadata, the notices, the attachments, the iter, and the text in
 * chunks. `apiBase` is the api's origin for the download links. What an
 * origin adds (Parliament's own data) is for its own chapter: nothing here
 * reads `fields` or `watch`.
 */
export function toItemPage(
  detail: ItemDetail,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
  apiBase = "",
  { typeLabel = (itemType) => itemType, revisions = true }: ItemPageOptions = {},
): ItemPage {
  const news = toNewsItem(detail, tree, search);
  const metadata: string[] = [];
  if (detail.item_type) metadata.push(typeLabel(detail.item_type));
  if (detail.doc_number) metadata.push(detail.doc_number);
  if (detail.legislature) metadata.push(`Legislatura ${detail.legislature}`);
  const chamber = chamberLabel(detail.chamber);
  if (chamber) metadata.push(chamber);
  if (revisions && detail.revision_count > 0)
    metadata.push(
      detail.revision_count === 1 ? "1 versione precedente" : `${detail.revision_count} versioni precedenti`,
    );
  if (revisions && detail.last_changed_at) metadata.push(`Ultima modifica ${formatDateTime(detail.last_changed_at)}`);

  const notices: ReaderNotice[] = [];
  const missing = !detail.has_text || !detail.text?.trim();
  if (missing) notices.push("missing");
  else if (isTextIncomplete(detail)) notices.push("incomplete");
  if (detail.files.some((f) => f.needs_manual_read)) notices.push("manual-read");

  const files: ReaderFile[] = detail.files.map((f) => ({
    id: f.file_id,
    name: f.filename ?? f.role,
    kind: fileKind(f.content_type),
    size: fileSize(f.size_bytes),
    status: f.needs_manual_read ? "da leggere a mano" : f.has_text ? "testo estratto" : "",
    downloadUrl: fileDownloadUrl(apiBase, detail, f),
  }));

  const iter: IterRow[] = (detail.history ?? [])
    .map((step) => ({
      text: [step.label, step.date ? formatLongDate(step.date) : null, chamberLabel(step.chamber)]
        .filter((part): part is string => Boolean(part))
        .join(" · "),
      current: step.is_current === true,
    }))
    .filter((row) => row.text !== "");

  const chunks = missing ? [] : toReaderChunks(toReaderBlocks(detail.text));
  return {
    news,
    metadata,
    notices,
    files,
    iter,
    chunks,
    totalChars: chunks.reduce((sum, chunk) => sum + chunk.chars, 0),
  };
}

// ---- Fonti ------------------------------------------------------------------

export interface SourceTree {
  active: Source[];
  deleted: Source[];
  totalActive: number;
}

const PARLIAMENT = "parlamento";
const CHAMBER_NAMES = { camera: "Camera", senato: "Senato" } as const;
const ORG_PREFIX = "org:";

const isParliament = (org: CatalogueOrganisation) => org.name.trim().toLowerCase() === PARLIAMENT;

/** The id of an organisation's node: `org:<name>`; the chambers are `parlamento:camera|senato`, the sections their key. */
export const orgNodeId = (name: string) => `${ORG_PREFIX}${name}`;

/**
 * «Camera, Plenaria» under the Camera branch is «Plenaria»: the catalogue
 * names a parliamentary section after its chamber, the tree already does.
 */
export function sectionLabel(section: Pick<CatalogueSection, "section" | "chamber">): string {
  if (!section.chamber) return section.section;
  const prefix = `${CHAMBER_NAMES[section.chamber].toLowerCase()},`;
  const name = section.section.trim();
  return name.toLowerCase().startsWith(prefix) ? name.slice(prefix.length).trim() || name : name;
}

function toSubSource(section: CatalogueSection): SubSource {
  return {
    id: section.key,
    name: sectionLabel(section),
    ...(section.health === "warning" ? { failing: true } : {}),
    ...(section.health_detail ? { healthDetail: section.health_detail } : {}),
  };
}

/** Parlamento's sections grouped under Camera and Senato; sections without a chamber stay at the top. */
function parliamentChildren(sections: CatalogueSection[]): SubSource[] {
  const byChamber: Record<"camera" | "senato", SubSource[]> = { camera: [], senato: [] };
  const loose: SubSource[] = [];
  for (const s of sections) {
    if (s.chamber) byChamber[s.chamber].push(toSubSource(s));
    else loose.push(toSubSource(s));
  }
  const chambers = (["camera", "senato"] as const)
    .filter((c) => byChamber[c].length > 0)
    .map((c) => ({
      id: `${PARLIAMENT}:${c}`,
      name: CHAMBER_NAMES[c],
      ...(c === "camera" ? { defaultOpen: true } : {}),
      children: byChamber[c],
    }));
  return [...chambers, ...loose];
}

/** The tooltip of an organisation's warning: its broken sections, each with its `health_detail`. */
function warningDetail(sections: CatalogueSection[]): string | undefined {
  const broken = sections.filter((s) => s.health === "warning");
  if (broken.length === 0) return undefined;
  return broken.map((s) => (s.health_detail ? `${s.section}: ${s.health_detail}` : s.section)).join("\n");
}

function toSource(org: CatalogueOrganisation, sections: CatalogueSection[]): Source {
  const children = isParliament(org) ? parliamentChildren(sections) : sections.map(toSubSource);
  const detail = org.health === "warning" ? warningDetail(sections) : undefined;
  return {
    id: orgNodeId(org.name),
    name: org.name,
    ...(org.health === "warning" ? { warning: true } : {}),
    ...(detail ? { healthDetail: detail } : {}),
    ...(org.health === "ok" ? { hasCode: true } : {}),
    // the pin is on as soon as one section is a favourite; «some» says the others are not
    ...(org.favourite !== "none" ? { favorite: true } : {}),
    ...(org.favourite === "some" ? { favoritePartly: true } : {}),
    ...(children.length > 0 ? { children } : {}),
  };
}

const byName = (a: Source, b: Source) => a.name.localeCompare(b.name, "it");

/**
 * The catalogue as the sources panel takes it: Parlamento first, then the
 * other organisations alphabetically; removed sections (and organisations)
 * under «Fonti eliminate». A removed organisation is one line, without its
 * sections; a live one with removed sections lists them.
 */
export function toSourceTree(catalogue: Catalogue): SourceTree {
  const active: Source[] = [];
  const deleted: Source[] = [];
  for (const org of catalogue.organisations) {
    const live = org.sections.filter((s) => s.removed_at === null);
    const gone = org.sections.filter((s) => s.removed_at !== null);
    if (org.removed) deleted.push({ id: orgNodeId(org.name), name: org.name });
    else {
      if (live.length > 0) active.push(toSource(org, live));
      if (gone.length > 0) deleted.push(toSource(org, gone));
    }
  }
  const parliament = active.filter((s) => s.name.trim().toLowerCase() === PARLIAMENT);
  const others = active.filter((s) => s.name.trim().toLowerCase() !== PARLIAMENT).sort(byName);
  return { active: [...parliament, ...others], deleted: deleted.sort(byName), totalActive: catalogue.total_active };
}

/** Every section key under a node of the tree (the node's own id when it is a section). */
export function subtreeKeys(node: Source | SubSource): string[] {
  if (!node.children || node.children.length === 0) return node.id.startsWith(ORG_PREFIX) ? [] : [node.id];
  return node.children.flatMap(subtreeKeys);
}

/** The keys of every active section of the tree. */
export function activeKeys(tree: Pick<SourceTree, "active">): string[] {
  return tree.active.flatMap(subtreeKeys);
}

/** The keys of the favourite sections of the catalogue. */
export function favouriteKeys(catalogue: Catalogue): string[] {
  return catalogue.organisations.flatMap((o) =>
    o.sections.filter((s) => s.favourite && s.removed_at === null).map((s) => s.key),
  );
}

/**
 * The checkboxes of the panel: a section is checked when its key is in
 * `keys` (the filter of ?sources=, or the favourites), an organisation or a
 * chamber when every section under it is.
 */
export function toSelection(tree: Pick<SourceTree, "active">, favourites: Iterable<string>): Record<string, boolean> {
  const set = new Set(favourites);
  const out: Record<string, boolean> = {};
  const visit = (node: Source | SubSource): boolean => {
    const keys = subtreeKeys(node);
    const checked = keys.length > 0 && keys.every((k) => set.has(k));
    out[node.id] = checked;
    for (const child of node.children ?? []) visit(child);
    return checked;
  };
  for (const org of tree.active) visit(org);
  return out;
}

/** The catalogue after a follow change, as the api will answer it: for the optimistic update and its rollback. */
export function applyFollowChange(catalogue: Catalogue, change: FollowChange): Catalogue {
  const add = new Set(change.add ?? []);
  const remove = new Set(change.remove ?? []);
  const organisations = catalogue.organisations.map((org) => {
    const sections = org.sections.map((s) =>
      add.has(s.key) ? { ...s, favourite: true } : remove.has(s.key) ? { ...s, favourite: false } : s,
    );
    const live = sections.filter((s) => s.removed_at === null);
    const fav = live.filter((s) => s.favourite).length;
    const favourite: CatalogueOrganisation["favourite"] = fav === 0 ? "none" : fav === live.length ? "all" : "some";
    return { ...org, sections, favourite };
  });
  return { ...catalogue, organisations };
}

/** The change that undoes another: the rollback of a refused follow. */
export function inverseFollowChange(change: FollowChange): FollowChange {
  return { add: change.remove ?? [], remove: change.add ?? [] };
}

/**
 * The filter of ?sources= after a checkbox changes: the node's subtree in or
 * out of the current filter (null: every active section). Null again when
 * everything is ticked (the plain URL), an empty list when nothing is
 * (?sources=none). Keys the tree no longer has are dropped; the order is the
 * tree's, so the same filter is always written the same way.
 */
export function keysAfterToggle(
  current: string[] | null,
  allActive: string[],
  node: Source | SubSource,
  checked: boolean,
): string[] | null {
  const set = new Set(current ?? allActive);
  for (const key of subtreeKeys(node)) {
    if (checked) set.add(key);
    else set.delete(key);
  }
  const next = allActive.filter((key) => set.has(key));
  return next.length === allActive.length ? null : next;
}

/** «Seleziona i preferiti»: the favourite sections, in the tree's order. */
export function favouriteSelection(tree: Pick<SourceTree, "active">, favourites: Iterable<string>): string[] {
  const set = new Set(favourites);
  return activeKeys(tree).filter((key) => set.has(key));
}

/** «Seleziona gli altri»: every active section that is not a favourite, in the tree's order. */
export function otherSelection(tree: Pick<SourceTree, "active">, favourites: Iterable<string>): string[] {
  const set = new Set(favourites);
  return activeKeys(tree).filter((key) => !set.has(key));
}

const sameSet = (a: readonly string[], b: readonly string[]) => {
  const x = new Set(a);
  const y = new Set(b);
  return x.size === y.size && [...x].every((k) => y.has(k));
};

/**
 * The quick selection the filter stands at, for aria-pressed: «tutte» when
 * the filter is null (or names every active section), «nessuna» when it is
 * empty, «preferiti» / «altri» when it is exactly that set; null otherwise.
 */
export function bulkMode(selection: string[] | null, favourites: string[], allActive: string[]): BulkMode | null {
  if (selection === null || sameSet(selection, allActive)) return "all";
  if (selection.length === 0) return "none";
  if (favourites.length > 0 && sameSet(selection, favourites)) return "favorites";
  const others = allActive.filter((key) => !favourites.includes(key));
  if (others.length > 0 && sameSet(selection, others)) return "others";
  return null;
}

/** The keys of a node that are not favourites yet: what the pin's PUT adds. */
export function missingKeys(node: Source | SubSource, favourites: Iterable<string>): string[] {
  const set = new Set(favourites);
  return subtreeKeys(node).filter((key) => !set.has(key));
}

/** The keys of a node that are favourites: what a folder can hold, what «Rimuovi dai preferiti» removes. */
export function heldKeys(node: Source | SubSource, favourites: Iterable<string>): string[] {
  const set = new Set(favourites);
  return subtreeKeys(node).filter((key) => set.has(key));
}

export const FOLLOW_FAILED = "Modifica dei preferiti non riuscita.";
export const UNKNOWN_SOURCE = "Alcune fonti non sono più nel catalogo: le fonti vengono aggiornate.";

/**
 * What the panel says of a refused PUT /me/sources, and whether «Riprova»
 * makes sense: a 422 names a key the catalogue no longer has (the catalogue
 * is read again), anything else can be sent again as it is.
 */
export function followErrorMessage(error: unknown): { message: string; retry: boolean } {
  if (isApiError(error) && error.status === 422) return { message: UNKNOWN_SOURCE, retry: false };
  return { message: FOLLOW_FAILED, retry: true };
}

/**
 * The name of the node whose sections are exactly `keys`, for «Ultime news ·
 * <nome>»: an organisation by its name, a chamber or a section with the
 * organisation before it («Parlamento · Camera», «ASI · News»). Null when no
 * node matches (a hand-written URL).
 */
export function nodeLabel(tree: Pick<SourceTree, "active">, keys: string[]): string | null {
  const wanted = [...new Set(keys)].sort().join("\n");
  const same = (node: Source | SubSource) => [...new Set(subtreeKeys(node))].sort().join("\n") === wanted;
  const find = (node: SubSource, path: string[]): string | null => {
    if (same(node)) return [...path, node.name].join(" · ");
    for (const child of node.children ?? []) {
      const hit = find(child, [...path, node.name]);
      if (hit) return hit;
    }
    return null;
  };
  if (wanted === "") return null;
  for (const org of tree.active) {
    if (same(org)) return org.name;
    for (const child of org.children ?? []) {
      const hit = find(child, [org.name]);
      if (hit) return hit;
    }
  }
  return null;
}

// ---- News salvate -----------------------------------------------------------

/**
 * The heading of a month's group: the month's name, with the year after it
 * when the year is not the current one («Ottobre», «Dicembre 2025»), so two
 * years never share a heading. The current year is Rome's, injectable for
 * the tests.
 */
export function savedGroupTitle(month: string, currentYear = romeYear()): string {
  const name = monthName(month);
  const year = month.slice(0, 4);
  return year === String(currentYear) ? name : `${name} ${year}`;
}

/**
 * The saved cards grouped by month (YYYY-MM), newest first, as the saved tab
 * shows them. The date on the right of the month stays empty: what it should
 * say is not decided yet (brief F4). The chips lead to the sources' views as
 * in the feed (toNewsItem).
 */
export function toSavedGroups(
  cards: SavedCard[],
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
  currentYear = romeYear(),
): SavedGroup[] {
  const groups = new Map<string, NewsItem[]>();
  for (const card of cards) {
    const items = groups.get(card.month) ?? [];
    items.push(toNewsItem(card, tree, search));
    groups.set(card.month, items);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([month, items]) => ({ month: savedGroupTitle(month, currentYear), dateLabel: "", items }));
}

/** A week of a month in the date filter: its id (YYYY-MM-Www) and its days («5–11 ottobre»). */
export interface SavedWeek {
  id: string;
  name: string;
}

/** A month of the date filter: its id (YYYY-MM), its Italian name and its weeks, newest first. */
export interface SavedMonth {
  id: string;
  name: string;
  weeks: SavedWeek[];
}

/** A year of the date filter: its months, newest first. */
export interface SavedYear {
  year: string;
  months: SavedMonth[];
}

/**
 * The facets as the date filter takes them: years newest first, months
 * newest first, each month's weeks as the api orders them (newest first by
 * their first day: in December 2025 «29–31 dicembre», ISO week 1 of 2026,
 * comes before «22–28 dicembre»); no counts.
 */
export function toSavedYears(page: Pick<SavedPage, "facets">): SavedYear[] {
  return [...page.facets]
    .sort((a, b) => b.year - a.year)
    .map((f) => ({
      year: String(f.year),
      months: [...f.months]
        .sort((a, b) => (a.month < b.month ? 1 : -1))
        .map((m) => ({
          id: m.month,
          name: monthName(m.month),
          weeks: m.weeks.map((w) => ({ id: w.week, name: weekLabel(w.from, w.to) })),
        })),
    }));
}

/** The leaves of the date filter: every week in the panel's order, and the weeks of each month. */
export interface SavedFacetIndex {
  weeks: string[];
  byMonth: Record<string, string[]>;
}

export function facetIndex(page: Pick<SavedPage, "facets">): SavedFacetIndex {
  const byMonth: Record<string, string[]> = {};
  for (const year of toSavedYears(page)) for (const m of year.months) byMonth[m.id] = m.weeks.map((w) => w.id);
  return { weeks: Object.values(byMonth).flat(), byMonth };
}

/**
 * The weeks a filter ticks: every week when it names neither months nor
 * weeks; else the weeks of the named months plus the named weeks. Ids the
 * facets no longer have are dropped.
 */
export function tickedWeeks(filter: SavedFilter, index: SavedFacetIndex): Set<string> {
  if (filter.months === null && filter.weeks === null) return new Set(index.weeks);
  const ticked = new Set<string>();
  for (const month of filter.months ?? []) for (const week of index.byMonth[month] ?? []) ticked.add(week);
  for (const week of filter.weeks ?? []) if (index.weeks.includes(week)) ticked.add(week);
  return ticked;
}

/**
 * The checkboxes of the date filter: a week is ticked when the filter names
 * it, its month, or nothing at all; a month is ticked when every week of it
 * is, "mixed" when some are, unticked when none is.
 */
export function savedSelection(filter: SavedFilter, index: SavedFacetIndex): Selection {
  const ticked = tickedWeeks(filter, index);
  const out: Selection = {};
  for (const [month, weeks] of Object.entries(index.byMonth)) {
    const n = weeks.filter((w) => ticked.has(w)).length;
    out[month] = n > 0 && n === weeks.length ? true : n > 0 ? "mixed" : false;
    for (const week of weeks) out[week] = ticked.has(week);
  }
  return out;
}

/**
 * The filter after a checkbox changes: a month takes every week of it in or
 * out, a week itself. Written as short as it reads: a month whose weeks are
 * all ticked goes to `months`, one ticked in part puts its weeks in `weeks`,
 * in the facets' order, so the same filter is always written the same way.
 * Both null when everything is ticked (the plain URL) and when nothing is
 * left (everything comes back ticked: no month means every month, the
 * user's decision). An id the facets do not have changes nothing.
 */
export function nextSavedFilter(
  filter: SavedFilter,
  index: SavedFacetIndex,
  id: string,
  checked: boolean,
): SavedFilter {
  const ticked = tickedWeeks(filter, index);
  const leaves = index.byMonth[id] ?? (index.weeks.includes(id) ? [id] : []);
  for (const week of leaves)
    if (checked) ticked.add(week);
    else ticked.delete(week);
  if (ticked.size === 0 || ticked.size === index.weeks.length) return { months: null, weeks: null };
  const months: string[] = [];
  const weeks: string[] = [];
  for (const [month, all] of Object.entries(index.byMonth)) {
    const some = all.filter((w) => ticked.has(w));
    if (some.length === all.length) months.push(month);
    else weeks.push(...some);
  }
  return { months: months.length > 0 ? months : null, weeks: weeks.length > 0 ? weeks : null };
}

// ---- Segnalibro -------------------------------------------------------------

/** One bookmark change: the item and the state the api is asked for. */
export interface SavedChange {
  origin: Origin;
  id: string;
  saved: boolean;
}

const isSameItem = (card: Pick<FeedCard, "origin" | "id">, change: SavedChange) =>
  card.origin === change.origin && card.id === change.id;

/** The card of a feed row that is the changed item: a sitting's row carries no bookmark (they are per document). */
const isChangedCard = (item: FeedItem, change: SavedChange): item is FeedCard =>
  item.kind === "card" && isSameItem(item, change);

/** A page of the feed with the bookmark of one item as the api will answer it. */
export function applySavedToFeed(page: FeedPage, change: SavedChange): FeedPage {
  if (!page.items.some((c) => isChangedCard(c, change))) return page;
  return { ...page, items: page.items.map((c) => (isChangedCard(c, change) ? { ...c, saved: change.saved } : c)) };
}

/** An item's detail with its bookmark as the api will answer it. */
export function applySavedToDetail(detail: ItemDetail, change: SavedChange): ItemDetail {
  return isSameItem(detail, change) && detail.saved !== change.saved ? { ...detail, saved: change.saved } : detail;
}

/** A saved card of a feed row; a sitting's row has no bookmark. */
const isSavedCard = (item: FeedItem): item is FeedCard => item.kind === "card" && item.saved;

/** A feed page with no bookmark left («Pulisci tutto»): the same page when none was saved. */
export function withoutBookmarks(page: FeedPage): FeedPage {
  if (!page.items.some(isSavedCard)) return page;
  return { ...page, items: page.items.map((c) => (isSavedCard(c) ? { ...c, saved: false } : c)) };
}

/** An item's detail with no bookmark («Pulisci tutto»): the same detail when it was not saved. */
export function withoutBookmark(detail: ItemDetail): ItemDetail {
  return detail.saved ? { ...detail, saved: false } : detail;
}

export type SittingDetail = components["schemas"]["SittingDetail"];

/** A sitting of the Parliament with the bookmark of one of its documents as the api will answer it. */
export function applySavedToSitting(detail: SittingDetail, change: SavedChange): SittingDetail {
  if (!detail.documents.some((c) => isSameItem(c, change))) return detail;
  return {
    ...detail,
    documents: detail.documents.map((c) => (isSameItem(c, change) ? { ...c, saved: change.saved } : c)),
  };
}

/** A sitting with no bookmark left on its documents («Pulisci tutto»). */
export function withoutBookmarksInSitting(detail: SittingDetail): SittingDetail {
  if (!detail.documents.some((c) => c.saved)) return detail;
  return { ...detail, documents: detail.documents.map((c) => (c.saved ? { ...c, saved: false } : c)) };
}

/** News salvate with nothing in it, as the api answers it after «Pulisci tutto»: no card, no facet, one page. */
export const clearedSavedPage = (): SavedPage => ({ facets: [], items: [], next_cursor: null });

/**
 * The pages of News salvate after a bookmark goes: the card leaves its page
 * and the counts of its month and of its week drop on every page's facets
 * (the week and the month leave at zero, as the api answers it). A bookmark
 * added changes nothing here: where the card stands in the list comes with
 * the refetch. Pages that do not hold the card stay as they are.
 */
export function applySavedToPages(pages: SavedPage[], change: SavedChange): SavedPage[] {
  if (change.saved) return pages;
  const card = pages.flatMap((p) => p.items).find((c) => isSameItem(c, change));
  if (!card) return pages;
  return pages.map((page) => ({
    ...page,
    facets: page.facets
      .map((f) => ({
        ...f,
        months: f.months
          .map((m) =>
            m.month === card.month
              ? {
                  ...m,
                  count: m.count - 1,
                  weeks: m.weeks
                    .map((w) => (w.week === card.week ? { ...w, count: w.count - 1 } : w))
                    .filter((w) => w.count > 0),
                }
              : m,
          )
          .filter((m) => m.count > 0),
      }))
      .filter((f) => f.months.length > 0),
    items: page.items.filter((c) => !isSameItem(c, change)),
  }));
}
